import * as React from "react";
import {Creatable} from 'react-select';
import {Option, option} from "ts-option";
import {PlaceholderProps} from "../../common/props/PlaceholderProps";
import {LoadableProps} from "../../common/props/LoadableProps";
import {ClearableProps} from "../../common/props/ClearableProps";
import {InputActionMeta} from "react-select/lib/types";
import {MultiValueRemove} from "react-select/lib/components/MultiValue";

export interface SelectComponentComponentProps<T> {
	/**
	 * All items.
	 */
	items: Option<T[]>;
	/**
	 * Selected
	 */
	value: Option<T | T[]>;
	/**
	 * The value of the search input.
	 */
	inputValue?: string;
	/**
	 * Callback on changed selection.
	 * @param selected element.
	 */
	onChanged: (selected: Option<T | T[]>) => void;
	/**
	 * Handle change events on the input.
	 * @param {string} value
	 * @param {InputActionMeta} action
	 */
	onInputChange?: (value: string, action: InputActionMeta) => void;
	/**
	 * If multiselect should by able
	 */
	multi?: boolean
	/**
	 * Add new option callback
	 */
	onAddOption: (newOption: string) => void
	/**
	 * Clearable flag.
	 */
	clearable?: boolean
	/**
	 * No results default text.
	 */
	noResultsText?: string
	/**
	 * Prompt text creator
	 * @param {string} filterText
	 * @returns {string}
	 */
	promptTextCreator?: (filterText: string) => React.ReactNode;
	/**
	 * On input key down callback
	 */
	onInputKeyDown?: () => void
	/**
	 * Returns the data for the new option when it is created.
	 * @param {string} value
	 * @param {string} label
	 * @returns {T}
	 */
	getNewOptionData?: (value: string, label: string) => T;
	/**
	 * Determines whether the "create new ..." option should be displayed based on the current input value, select value and options array.
	 * @param {string} value
	 * @returns {boolean}
	 */
	isValidNewOption?: (value: string) => boolean;
	/**
	 * Backspace removes value
	 */
	backspaceRemovesValue?: boolean
}

type Props<T> = SelectComponentComponentProps<T> & PlaceholderProps & LoadableProps & ClearableProps

/**
 * Component for creation.
 */
export default abstract class CreatableComponent<T> extends React.PureComponent<Props<T>, {}> {
	public static defaultProps: Partial<Props<any>> = {
		isValidNewOption: () => true
	};

	render() {
		return (
			<Creatable
				value={this.props.value.orNull}
				classNamePrefix={"c-select-component"}
				inputValue={this.props.inputValue}
				options={this.props.items.getOrElse([])}
				styles={{
					indicatorsContainer: (base) => ({...base, display: 'none'})
				}}
				onChange={(val?: T | T[]) => this.props.onChanged(option(val))}
				isMulti={this.props.multi}
				onCreateOption={this.props.onAddOption}
				placeholder={this.props.placeholder}
				isClearable={this.props.clearable}
				noOptionsMessage={() => this.props.noResultsText}
				onInputChange={this.props.onInputChange}
				getOptionValue={this.elementToText}
				getOptionLabel={this.elementToText}
				onKeyDown={this.props.onInputKeyDown}
				isLoading={this.props.loading}
				tabSelectsValue={false}
				isValidNewOption={this.props.isValidNewOption}
				formatCreateLabel={(filterText) => this.props.promptTextCreator(filterText)}
				createOptionPosition={"last"}
				closeMenuOnSelect={false}
				openMenuOnFocus={false}
				getNewOptionData={this.props.getNewOptionData}
				components={{MultiValueRemove: this.renderMultiValueRemove}}
				backspaceRemovesValue={this.props.backspaceRemovesValue}/>
		)
	}

	renderMultiValueRemove(props: any) {
		return <MultiValueRemove {...props}/>
	}

	/**
	 * Convert element to it's display text for query input.
	 * @param {T} element element to convert.
	 * @return {string} display text from element.
	 */
	abstract elementToText(element: T): string;
}